import { __decorate } from "tslib";
// ** Dependencies **
import '../time-slider/define';
import '../seekable-progress-bar/define';
import { html, LitElement } from 'lit';
import { property } from 'lit/decorators.js';
import { createRef, ref } from 'lit/directives/ref.js';
import { provideContextRecord, watchContext } from '../../base/context';
import { ifNonEmpty } from '../../base/directives';
import { WithFocus } from '../../base/elements';
import { DisposalBin, eventListener, listen, redispatchEvent } from '../../base/events';
import { ElementLogger } from '../../base/logger';
import { DEV_MODE } from '../../global/env';
import { mediaContext } from '../../media';
import { buildExportPartsAttr, setAttribute } from '../../utils/dom';
import { isNil } from '../../utils/unit';
import { SeekableProgressBarElement } from '../seekable-progress-bar';
import { TimeSliderElement } from '../time-slider';
import { scrubberContext } from './context';
import { scrubberElementStyles } from './styles';
export const SCRUBBER_ELEMENT_TAG_NAME = 'vds-scrubber';
/**
 * A control that displays the progression of playback and the amount seekable on a slider. This
 * control can be used to update the current playback time by interacting with the slider.
 *
 * 💡 The following attributes are updated for your styling needs:
 *
 * - `media-can-play`: Applied when media can begin playback.
 * - `media-waiting`: Applied when playback has stopped because of a lack of temporary data.
 *
 * 💡 See the `<vds-scrubber-preview>` element if you'd like to include previews.
 *
 * @tagname vds-scrubber
 * @csspart time-slider - The time slider (`<vds-time-slider>`).
 * @csspart time-slider-* - All `vds-time-slider` parts re-exported with the `time-slider` prefix.
 * @csspart progress-bar - The progress bar (`<vds-seekable-progress-bar>`).
 * @csspart progress-bar-* - All `vds-seekable-progress-bar` parts re-exported with the `progress-bar` prefix.
 * @slot Used to pass content into the slider.
 * @slot progress-bar - Used to pass content into the progress bar.
 */
export class ScrubberElement extends WithFocus(LitElement) {
    constructor() {
        super(...arguments);
        // -------------------------------------------------------------------------------------------
        // Properties
        // -------------------------------------------------------------------------------------------
        /* c8 ignore next */
        this._logger = DEV_MODE && new ElementLogger(this);
        this.ctx = provideContextRecord(this, scrubberContext);
        /**
         * Whether the scrubber should be disabled (not-interactable).
         */
        this.disabled = false;
        /**
         * Whether the scrubber should be hidden.
         */
        this.hidden = false;
        /**
         * ♿ **ARIA:** The `aria-label` for the time slider.
         */
        this.label = 'Media time slider';
        /**
         * The time slider orientation.
         */
        this.orientation = 'horizontal';
        /**
         * ♿ **ARIA:** The `aria-label` for the progress bar.
         */
        this.progressLabel = 'Amount of seekable media';
        /**
         * ♿ **ARIA:** Human-readable text alternative for the progress bar value. If you pass
         * in a string containing `{seekableAmount}` or `{duration}` templates they'll be replaced with
         * the spoken form such as `1 hour 30 minutes`.
         */
        this.progressValueText = '{seekableAmount} out of {duration}';
        /**
         * Whether the scrubber should request playback to pause while the user is dragging the
         * thumb. If the media was playing before the dragging starts, the state will be restored by
         * dispatching a user play request once the dragging ends.
         */
        this.pauseWhileDragging = false;
        /**
         * A number that specifies the granularity that the time slider value must adhere to in seconds.
         * For example, a step with the value `1` indicates a granularity of 1 second increments.
         */
        this.step = 0.25;
        /**
         * ♿ **ARIA:** A number that specifies the number of steps taken when interacting with
         * the time slider via keyboard. Think of it as `step * keyboardStep`.
         */
        this.keyboardStep = 20;
        /**
         * ♿ **ARIA:** A number that will be used to multiply the `keyboardStep` when the `Shift` key
         * is held down and the slider value is changed by pressing `LeftArrow` or `RightArrow`. Think
         * of it as `keyboardStep * shiftKeyMultiplier`.
         */
        this.shiftKeyMultiplier = 2;
        /**
         * The amount of milliseconds to throttle media seeking request events being dispatched.
         */
        this.seekingRequestThrottle = 100;
        /**
         * ♿ **ARIA:** Human-readable text alternative for the time slider value. If you pass
         * in a string containing `{currentTime}` or `{duration}` templates they'll be replaced with
         * the spoken form such as `1 hour 30 minutes`.
         */
        this.valueText = '{currentTime} out of {duration}';
        // -------------------------------------------------------------------------------------------
        // Time Slider
        // -------------------------------------------------------------------------------------------
        this._timeSliderRef = createRef();
        // -------------------------------------------------------------------------------------------
        // Seekable Progress Bar
        // -------------------------------------------------------------------------------------------
        this._progressBarRef = createRef();
        this._scrubberPreviewDisconnectDisposal = new DisposalBin();
    }
    static get styles() {
        return [scrubberElementStyles];
    }
    static get parts() {
        const timeSliderExports = TimeSliderElement.parts.map((part) => `time-slider-${part}`);
        const seekableProgressBarExports = SeekableProgressBarElement.parts.map((part) => `progress-bar-${part}`);
        return [
            'time-slider',
            'progress-bar',
            ...timeSliderExports,
            ...seekableProgressBarExports
        ];
    }
    _handleCanPlayContextUpdate(canPlay) {
        setAttribute(this, 'media-can-play', canPlay);
    }
    _handleWaitingContextUpdate(waiting) {
        setAttribute(this, 'media-waiting', waiting);
    }
    // -------------------------------------------------------------------------------------------
    // Lifecycle
    // -------------------------------------------------------------------------------------------
    update(changedProperties) {
        var _a;
        if (changedProperties.has('disabled')) {
            if (this.disabled) {
                (_a = this.scrubberPreviewElement) === null || _a === void 0 ? void 0 : _a.hidePreview();
            }
            if (!isNil(this.scrubberPreviewElement)) {
                this.scrubberPreviewElement.disabled = this.disabled;
            }
        }
        if (changedProperties.has('hidden')) {
            if (!isNil(this.scrubberPreviewElement)) {
                this.scrubberPreviewElement.hidden = this.hidden;
            }
        }
        super.update(changedProperties);
    }
    render() {
        return html `${this._renderTimeSlider()}`;
    }
    // -------------------------------------------------------------------------------------------
    // Pointer Events
    // -------------------------------------------------------------------------------------------
    _handlePointerEnter(event) {
        var _a;
        if (this.disabled)
            return;
        this.ctx.pointing = true;
        this.setAttribute('pointing', '');
        /* c8 ignore start */
        if (DEV_MODE) {
            this._logger
                .debugGroup('pointer enter')
                .appendWithLabel('Event', event)
                .end();
        }
        /* c8 ignore stop */
        (_a = this.scrubberPreviewElement) === null || _a === void 0 ? void 0 : _a.showPreview(event);
    }
    _handlePointerMove(event) {
        var _a;
        if (this.disabled || this.ctx.dragging)
            return;
        (_a = this.scrubberPreviewElement) === null || _a === void 0 ? void 0 : _a.updatePreviewPosition(event);
    }
    _handlePointerLeave(event) {
        var _a;
        if (this.disabled)
            return;
        this.ctx.pointing = false;
        this.removeAttribute('pointing');
        /* c8 ignore start */
        if (DEV_MODE) {
            this._logger
                .debugGroup('pointer leave')
                .appendWithLabel('Event', event)
                .end();
        }
        /* c8 ignore stop */
        if (!this.ctx.dragging) {
            (_a = this.scrubberPreviewElement) === null || _a === void 0 ? void 0 : _a.hidePreview(event);
        }
    }
    /**
     * Returns the underlying `vds-time-slider` component.
     */
    get timeSliderElement() {
        return this._timeSliderRef.value;
    }
    _renderTimeSlider() {
        return html `
      <vds-time-slider
        id="time-slider"
        exportparts=${buildExportPartsAttr(TimeSliderElement.parts, 'time-slider')}
        label=${ifNonEmpty(this.label)}
        orientation=${this.orientation}
        part="time-slider"
        step=${this.step}
        keyboard-step=${this.keyboardStep}
        shift-key-multiplier=${this.shiftKeyMultiplier}
        value-text=${this.valueText}
        ?disabled=${this.disabled}
        ?hidden=${this.hidden}
        ?pause-while-dragging=${this.pauseWhileDragging}
        @pointerenter=${this._handlePointerEnter}
        @pointermove=${this._handlePointerMove}
        @pointerleave=${this._handlePointerLeave}
        @vds-slider-drag-start=${this._handleSliderDragStart}
        @vds-slider-value-change=${this._handleSliderValueChange}
        @vds-slider-drag-end=${this._handleSliderDragEnd}
        ${ref(this._timeSliderRef)}
      >
        ${this._renderTimeSliderChildren()}
      </vds-time-slider>
    `;
    }
    _renderTimeSliderChildren() {
        return html `${this._renderDefaultSlot()}${this._renderProgressBar()}`;
    }
    _renderDefaultSlot() {
        return html `<slot></slot>`;
    }
    _handleSliderDragStart(event) {
        var _a;
        if (this.disabled)
            return;
        this.ctx.dragging = true;
        this.setAttribute('dragging', '');
        (_a = this.scrubberPreviewElement) === null || _a === void 0 ? void 0 : _a.showPreview(event);
        redispatchEvent(this, event);
    }
    _handleSliderValueChange(event) {
        var _a;
        if (this.disabled)
            return;
        (_a = this.scrubberPreviewElement) === null || _a === void 0 ? void 0 : _a.updatePreviewPosition(event);
        redispatchEvent(this, event);
    }
    _handleSliderDragEnd(event) {
        var _a;
        if (this.disabled)
            return;
        this.ctx.dragging = false;
        this.removeAttribute('dragging');
        if (!this.ctx.pointing)
            (_a = this.scrubberPreviewElement) === null || _a === void 0 ? void 0 : _a.hidePreview(event);
        redispatchEvent(this, event);
    }
    /**
     * Returns the underlying `<vds-seekable-progress-bar>` component.
     */
    get progressBarElement() {
        return this._progressBarRef.value;
    }
    _renderProgressBar() {
        return html `
      <vds-seekable-progress-bar
        id="progress-bar"
        part="progress-bar"
        exportparts=${buildExportPartsAttr(SeekableProgressBarElement.parts, 'progress-bar')}
        label=${this.progressLabel}
        value-text=${this.progressValueText}
        ${ref(this._progressBarRef)}
      >
        ${this._renderProgressBarChildren()}
      </vds-seekable-progress-bar>
    `;
    }
    _renderProgressBarChildren() {
        return this._renderProgressBarSlot();
    }
    _renderProgressBarSlot() {
        return html `<slot name="progress-bar"></slot>`;
    }
    /**
     * The scrubber preview element `<vds-scrubber-preview>` (if given).
     */
    get scrubberPreviewElement() {
        return this._scrubberPreviewElement;
    }
    _handlePreviewConnect(event) {
        event.stopPropagation();
        const { element, onDisconnect } = event.detail;
        this._scrubberPreviewElement = element;
        this.setAttribute('previewable', '');
        this._scrubberPreviewDisconnectDisposal.add(listen(element, 'vds-scrubber-preview-show', this._handlePreviewShow.bind(this)), listen(element, 'vds-scrubber-preview-time-update', this._handlePreviewTimeUpdate.bind(this)), listen(element, 'vds-scrubber-preview-hide', this._handlePreviewHide.bind(this)));
        onDisconnect(() => {
            this._scrubberPreviewDisconnectDisposal.empty();
            this._scrubberPreviewElement = undefined;
            this.removeAttribute('previewable');
        });
    }
    _handlePreviewShow(event) {
        event.stopPropagation();
        this.setAttribute('previewing', '');
    }
    _handlePreviewTimeUpdate(event) {
        event.stopPropagation();
    }
    _handlePreviewHide(event) {
        event.stopPropagation();
        this.removeAttribute('previewing');
    }
}
__decorate([
    property({ type: Boolean, reflect: true })
], ScrubberElement.prototype, "disabled", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], ScrubberElement.prototype, "hidden", void 0);
__decorate([
    property({ reflect: true })
], ScrubberElement.prototype, "label", void 0);
__decorate([
    property({ reflect: true })
], ScrubberElement.prototype, "orientation", void 0);
__decorate([
    property({ attribute: 'progress-label', reflect: true })
], ScrubberElement.prototype, "progressLabel", void 0);
__decorate([
    property({ attribute: 'progress-value-text' })
], ScrubberElement.prototype, "progressValueText", void 0);
__decorate([
    property({ attribute: 'pause-while-dragging', type: Boolean })
], ScrubberElement.prototype, "pauseWhileDragging", void 0);
__decorate([
    property({ type: Number })
], ScrubberElement.prototype, "step", void 0);
__decorate([
    property({ attribute: 'keyboard-step', type: Number })
], ScrubberElement.prototype, "keyboardStep", void 0);
__decorate([
    property({ attribute: 'shift-key-multiplier', type: Number })
], ScrubberElement.prototype, "shiftKeyMultiplier", void 0);
__decorate([
    property({ attribute: 'seeking-request-throttle', type: Number })
], ScrubberElement.prototype, "seekingRequestThrottle", void 0);
__decorate([
    property({ attribute: 'value-text' })
], ScrubberElement.prototype, "valueText", void 0);
__decorate([
    watchContext(mediaContext.canPlay)
], ScrubberElement.prototype, "_handleCanPlayContextUpdate", null);
__decorate([
    watchContext(mediaContext.waiting)
], ScrubberElement.prototype, "_handleWaitingContextUpdate", null);
__decorate([
    eventListener('vds-scrubber-preview-connect')
], ScrubberElement.prototype, "_handlePreviewConnect", null);
